import { getCookie, setCookie } from 'svelte-cookie';
import { closeModal, openModal } from './modalStore';
import { get, writable } from 'svelte/store';
import { page } from '$app/stores';
import { type CookieModalData } from '$lib/types/globalTypes';

/**
 * This Store is responsible for handling everthing related to tracking (GA, FB Pixel, etc.)
 *
 * It promts the user with a cookie banner where the settings can be configured and load only the configured data to be DSGVO conform
 */

/* CONFIG */

/** time after that the banner appears */
const BANNER_DELAY = 1000;

const COOKIE_CONSENT = 'cookies-consent';

/** the name of the cookie indicating that cookies have been configured */
const COOKIE_CONFIGURED = 'cookies-configured';

/** the name of the cookie indicating that that marketing cookies are enabled */
const COOKIE_MARKETING = 'cookies-marketing';

/** the name of the cookie indicating that statistical cookies are enabled */
const COOKIE_STATISTICS = 'cookies-statistics';

/* CODE */

export type ApiKeys = {
	googleAnalyticsId?: string;
	googleTagmanagerId?: string;
	facebookPixelId?: string;
};

export let keys: ApiKeys = {};
export let options: CookieModalData['options'] = [];
export let optionKeys: string[] = [];
let opened = false;

export function init(k: ApiKeys) {
	keys = k;
	options = (get(page).data?.cookieModal as CookieModalData)?.options ?? [];
	optionKeys = options?.map(({ cookie_option_id: option }) => option.key) ?? [];

	initConsent()

	const isSet = isSetup();


	if (isSet) {
		return;
	}

	function openCookieModal() {
		if (opened) return;

		openModal('CookieModalBar');
		opened = true;

		window.removeEventListener('scroll', openCookieModal);
		window.removeEventListener('click', openCookieModal);
	}

	function openCookieModalDelayed() {
		setTimeout(openCookieModal, BANNER_DELAY);
	}

	window.addEventListener('scroll', openCookieModalDelayed);
	window.addEventListener('click', openCookieModalDelayed);
}

export function getKeys() {
	return keys;
}

type Consent = Record<string, boolean>;

function isConsent(consent: any): consent is Consent {
	return typeof consent === 'object' && consent !== null;
}

function isConsentWithAllKeysConfigured(consent: any,): consent is Consent {
	const isKeyMissing = optionKeys.filter((key) => !Object.keys(consent).includes(key)).length !== 0;
	return isConsent(consent) && !isKeyMissing;
}

export function setupConsent(c: Consent, modalId?: string) {
	setCookie(COOKIE_CONSENT, JSON.stringify(c), 365, true);

	if (modalId) {
		closeModal(modalId);
	}

	consent.set(c);
}

export function acceptAll(modalId?: string) {
	const consent = options.map(({ cookie_option_id: option }) => ({ [option.key]: true })).reduce((acc, curr) => ({ ...acc, ...curr }), {});
	setupConsent(consent, modalId);
}

export function rejectAll(modalId?: string) {
	const consent = options.map(({ cookie_option_id: option }) => ({ [option.key]: option.mandatory })).reduce((acc, curr) => ({ ...acc, ...curr }), {});
	setupConsent(consent, modalId);
}

function getCookieOrUndefined(cookie: string) {
	try {
		return getCookie(cookie);
	} catch (error) {
		return undefined;
	}
}

export function isSetup() {
	return !!get(consent)
}

export const consent = writable<Consent | undefined>(undefined);

export function initConsent() {
	const cookie = getCookieOrUndefined(COOKIE_CONSENT)
	const parsedCookie = cookie ? JSON.parse(cookie) : undefined

	if (!parsedCookie || !isConsentWithAllKeysConfigured(parsedCookie)) return

	consent.set(parsedCookie)
}